import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Flame, Calendar, Trophy, TrendingUp } from "lucide-react";

export default function StreakTracker({ childId, childName }) {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!childId) return;
    axios.get(`${API}/achievements/streak/${childId}`)
      .then(res => setStreak(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [childId]);

  if (loading || !streak) return null;

  const dayStreak = streak.current_day_streak;
  const weekStreak = streak.current_week_streak;

  // Build last 7 days visual
  const today = new Date();
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (6 - i));
    return d.toLocaleDateString("en-US", { weekday: "short" }).charAt(0);
  });

  // Fire intensity based on streak length
  const fireColor = dayStreak >= 14 ? "#FF4500" : dayStreak >= 7 ? "#FF6B35" : dayStreak >= 3 ? "#F77F00" : "#FFD166";
  const fireBg = dayStreak >= 14 ? "#FFF0E0" : dayStreak >= 7 ? "#FFF3E6" : "#FFF8ED";

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="streak-tracker">
      {/* Header */}
      <div className="p-3 flex items-center gap-3"
        style={{ background: `linear-gradient(135deg, ${fireColor}, #EF476F)` }}>
        <div className="bg-white/20 rounded-xl p-2">
          <Flame size={22} color="white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white/80 text-xs font-bold uppercase tracking-wide">
            {childName || "My"} Streak
          </p>
          <div className="flex items-baseline gap-2">
            <span className="text-white font-black text-2xl" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {dayStreak}
            </span>
            <span className="text-white/80 font-bold text-sm">
              {dayStreak === 1 ? "day" : "days"}
            </span>
          </div>
        </div>
        {dayStreak >= 7 && (
          <div className="bg-white/25 rounded-full px-2 py-1">
            <span className="text-white font-black text-xs">{weekStreak}w</span>
          </div>
        )}
      </div>

      {/* Week dots */}
      <div className="px-3 py-2.5">
        <div className="flex justify-between gap-1">
          {last7.map((label, i) => {
            const isActive = i >= (7 - dayStreak);
            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                    isActive ? "shadow-md" : "bg-gray-100"
                  }`}
                  style={isActive ? { background: fireColor } : {}}
                  data-testid={`streak-day-${i}`}
                >
                  {isActive ? (
                    <Flame size={14} color="white" strokeWidth={2.5} />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                  )}
                </div>
                <span className="text-[10px] font-bold text-gray-400">{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats row */}
      <div className="px-3 pb-3 flex gap-2">
        {[
          { icon: TrendingUp, label: "Total", value: streak.total_lessons_completed, color: "#118AB2" },
          { icon: Trophy, label: "Best", value: `${streak.longest_day_streak}d`, color: "#F77F00" },
          { icon: Calendar, label: "Weeks", value: streak.total_weeks_completed, color: "#9B5DE5" },
        ].map((stat, i) => (
          <div key={i} className="flex-1 rounded-xl p-2 text-center" style={{ background: stat.color + "12" }}>
            <stat.icon size={14} color={stat.color} className="mx-auto mb-0.5" strokeWidth={2.5} />
            <p className="font-black text-sm" style={{ color: stat.color }}>{stat.value}</p>
            <p className="text-[10px] font-bold text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
